import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Paper,
  Stack,
  Chip,
  FormControlLabel,
  Checkbox,
  Autocomplete
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FormsBottomNavbar from './FormsBottomNavbar';
import SearchBar from './PostBack';
import { AuthContext } from '../../AuthContext/AuthContext';
import { GOOGLEMAPS_API_KEY } from '../../Api/ApiUrls';

// Styled Components
const FilterContainer = styled(Paper)(({ theme }) => ({
  padding: '16px',
  margin: '8px',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  backgroundColor: '#fff',
  minHeight: "75vh"
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '16px',
  marginBottom: '12px',
  color: '#333'
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#8B4513',
  color: 'white',
  fontWeight: 'bold',
  borderRadius: '8px',
  padding: '8px 16px',
  '&:hover': { backgroundColor: '#654321' },
}));

// Constants
const LOOKING_FOR_OPTIONS = ['Purchase', 'Rent', 'Lease', 'JV/JD', 'Build to Suit'];

const PROPERTY_TYPE_OPTIONS = {
  Purchase: ['Plot', 'Land', 'Rental with Duplex Building', 'Duplex House', 'PG', 'Office', 'Flat', 'Villas', 'Commercial Building', 'Apartment', 'Rental Building'],
  Rent: ['House', 'Duplex House', 'Villa', 'Commercial Plot', 'Commercial Land', 'Commercial Building', 'PG', 'Office', 'Flat'],
  Lease: ['House', 'Duplex House', 'Villa', 'Plot', 'Commercial Plot', 'Commercial Land', 'Commercial Building', 'PG', 'Office', 'Flat'],
  'JV/JD': ['Land', 'Plot'],
  'Build to Suit': ['Land', 'Plot']
};

const BUDGET_RANGES = {
  Purchase: ['No Min', '10L', '20L', '30L', '40L', '50L', '60L', '70L', '80L', '90L', '1CR', '1.25CR', '1.5CR', '1.75CR', '2CR', '2.5CR', '3CR', '3.5 CR', '4CR', '4.5CR', '5CR', '5.5CR', '6 CR', '8CR', '9CR', '10CR', '15CR', '20CR', '25CR', '30CR', '40CR', '50CR', '60CR', '70CR', '80CR', '90CR', '100CR+++'],
  Rent: ['No Min', '1K', '2K', '3K', '4K', '5K', '10K', '15K', '20K', '25K', '30K', '35K', '40K', '50K', '60K', '70K', '80K', '90K', '1L', '1.25L', '1.5 L', '2L', '2.5L', '3L', '3.5L', '4L', '4.5L', '5L', '6L', '7L', '8L', '9L', '10L', '15L', '20L', '25L', '30L', '30+++'],
  Lease: ['No Min', '1L', '2L', '3L', '4L', '5L', '10L', '15L', '20L', '25L', '30L', '35L', '40L', '50L', '60L', '70L', '80L', '90L', '1CR', '1.25CR', '1.5 CR', '2CR', '2.5CR', '3CR', '3.5CR', '4CR', '4.5CR', '5CR', '6CR', '7CR', '8CR', '9CR', '10CR', '15CR', '20CR', '25CR', '30CR', '30+++'],
  'JV/JD': ['No Min', '1L', '2L', '3L', '4L', '5L', '10L', '15L', '20L', '25L', '30L', '32L', '40L', '50L', '60L', '70L', '80L', '90L', '1CR', '1.25CR', '1.5 CR', '2CR', '2.5CR', '3CR', '3.5CR', '4CR', '4.5CR', '5CR', '6CR', '7CR', '8CR', '9CR', '10CR', '15CR', '20CR', '25CR', '30CR', '30+++'],
  'Build to Suit': ['No Min', '1L', '2L', '3L', '4L', '5L', '10L', '11L', '12L', '21L', '22L', '31L', '32L', '40L', '50L', '60L', '70L', '80L', '90L', '1CR', '1.25CR', '1.5 CR', '2CR', '2.5CR', '3CR', '3.5CR', '4CR', '4.5CR', '5CR', '6CR', '7CR', '8CR', '9CR', '10CR', '15CR', '20CR', '25CR', '30CR', '30+++']
};

const INITIAL_FORM_DATA = {
  length: '', width: '', units: 'ft', area: '', no_of_bedrooms: '',
  min_monthly_rent: '', max_monthly_rent: '', min_lease_per_year: '', max_lease_per_year: '',
  min_acres: '', max_acres: '', budget_per_acre: '', ratio: '', floor: '', expected_rental_income: ''
};

// Helper Functions
const convertBudgetToNumber = (budgetStr) => {
  if (!budgetStr || budgetStr === 'No Min') return 0;
  if (budgetStr.includes('K')) return parseFloat(budgetStr) * 1000;
  if (budgetStr.includes('L')) return parseFloat(budgetStr) * 100000;
  if (budgetStr.includes('CR')) return parseFloat(budgetStr) * 10000000;
  if (budgetStr.includes('+++')) return parseFloat(budgetStr) * 10000000;
  return parseFloat(budgetStr);
};

const PGRoom = () => {
  const navigate = useNavigate();
   const { userId } = useContext(AuthContext); // Replace with actual user ID from context
  
  const [lookingFor, setLookingFor] = useState('');
  const [locations, setLocations] = useState([]);
  const [locationDetails, setLocationDetails] = useState([]);
  const [propertyType, setPropertyType] = useState('');
  const [options, setOptions] = useState([]);
  const [minBudget, setMinBudget] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  // Google Maps Script
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLEMAPS_API_KEY}&libraries=places`;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const getAvailableMaxBudgets = () => {
    if (!lookingFor || !minBudget) return BUDGET_RANGES[lookingFor] || [];
    
    const allBudgets = BUDGET_RANGES[lookingFor];
    const minIndex = allBudgets.indexOf(minBudget);
    
    if (minIndex === -1) return allBudgets;
    return allBudgets.slice(minIndex + 1);
  };

  // Fetch geocoding for a location
  const getGeocode = async (locationName) => {
    return new Promise((resolve) => {
      if (!window.google) {
        resolve({ location: locationName, lat: "", long: "" });
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: locationName }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const lat = results[0].geometry.location.lat().toFixed(4);
          const lng = results[0].geometry.location.lng().toFixed(4);
          resolve({ location: locationName, lat, long: lng });
        } else {
          resolve({ location: locationName, lat: "", long: "" });
        }
      });
    });
  };

  // Fetch Places with predictions
  const fetchPlaces = (input) => {
    if (!input || !window.google) return;
    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      { input, types: ["geocode"], componentRestrictions: { country: "IN" } },
      (predictions) => {
        if (predictions) setOptions(predictions.map((p) => p.description));
      }
    );
  };

  // Handle location changes
  const handleLocationChange = async (event, newValue) => {
    setLocations(newValue);
    
    // Get geocoding for all locations
    const locationPromises = newValue.map(loc => getGeocode(loc));
    const results = await Promise.all(locationPromises);
    setLocationDetails(results);
  };

  const handleFormDataChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLookingForChange = (value) => {
    setLookingFor(value);
    setPropertyType('');
    setMinBudget('');
    setMaxBudget('');
    setFormData(INITIAL_FORM_DATA);
  };

  const handleSave = async () => {
    console.log("📌 Saving property request...");
    try {
      const payload = {
        looking_for: lookingFor.toLowerCase(),
        property_type: propertyType,
        length: parseFloat(formData.length) || 0,
        width: parseFloat(formData.width) || 0,
        units: formData.units,
        area: parseFloat(formData.area) || 0,
        min_budget: convertBudgetToNumber(minBudget),
        max_budget: convertBudgetToNumber(maxBudget),
        no_of_bedrooms: parseInt(formData.no_of_bedrooms) || 0,
        min_monthly_rent: parseFloat(formData.min_monthly_rent) || 0,
        max_monthly_rent: parseFloat(formData.max_monthly_rent) || 0,
        min_year_lease: parseFloat(formData.min_lease_per_year) || 0,
        max_year_lease: parseFloat(formData.max_lease_per_year) || 0,
        min_acres: parseFloat(formData.min_acres) || 0,
        max_acres: parseFloat(formData.max_acres) || 0,
        ratio: formData.ratio,
        floor: parseInt(formData.floor) || 0,
        comment: "Property request from form",
        user_id: userId,
        new_locations: locationDetails
      };

      console.log("📤 Payload:", JSON.stringify(payload, null, 2));

      const response = await fetch("https://landnest.net:81/property-request/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("✅ Property request saved successfully:", result);
        navigate(-1);
      } else {
        const errorText = await response.text();
        console.error("❌ Failed to save property request:", errorText);
        alert("Failed to save property request. Please try again.");
      }
    } catch (error) {
      console.error("🚨 Error saving property request:", error);
      alert("An error occurred while saving. Please try again.");
    }
  };

  const handleCancel = () => navigate(-1);
    const handleBackClick = () => navigate(-1);

  // Field Components
  const renderDimensionFields = () => (
    <Box key="dimensions" sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Dimensions</Typography>
      <Stack direction="row" spacing={1}>
        <TextField fullWidth label="Length" placeholder="e.g., 40" variant="outlined" size="small"
          value={formData.length} onChange={(e) => handleFormDataChange('length', e.target.value)} />
        <Typography sx={{ alignSelf: 'center' }}>×</Typography>
        <TextField fullWidth label="Width" placeholder="e.g., 60" variant="outlined" size="small"
          value={formData.width} onChange={(e) => handleFormDataChange('width', e.target.value)} />
        <TextField select label="Units" value={formData.units} size="small" sx={{ minWidth: 80 }}
          onChange={(e) => handleFormDataChange('units', e.target.value)}>
          <MenuItem value="ft">ft</MenuItem>
          <MenuItem value="m">m</MenuItem>
        </TextField>
      </Stack>
    </Box>
  );

  const renderRangeFields = (minField, maxField, label) => (
    <Box key={label} sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>{label}</Typography>
      <Stack direction="row" spacing={1}>
        <TextField fullWidth label={`Min ${label}`} placeholder="e.g., 10" variant="outlined" size="small"
          value={formData[minField]} onChange={(e) => handleFormDataChange(minField, e.target.value)} />
        <TextField fullWidth label={`Max ${label}`} placeholder="e.g., 15" variant="outlined" size="small"
          value={formData[maxField]} onChange={(e) => handleFormDataChange(maxField, e.target.value)} />
      </Stack>
    </Box>
  );

  const renderBedroomField = () => (
    <FormControl fullWidth key="bedrooms" sx={{ mb: 2 }} size="small">
      <InputLabel>Number of Bedrooms</InputLabel>
      <Select label="Number of Bedrooms" value={formData.no_of_bedrooms}
        onChange={(e) => handleFormDataChange('no_of_bedrooms', e.target.value)}>
        {[1, 2, 3, 4].map(num => <MenuItem key={num} value={num.toString()}>{num} BHK</MenuItem>)}
      </Select>
    </FormControl>
  );

  const renderTextField = (field, label, placeholder) => (
    <TextField key={field} fullWidth label={label} placeholder={placeholder} variant="outlined" size="small" sx={{ mb: 2 }}
      value={formData[field]} onChange={(e) => handleFormDataChange(field, e.target.value)} />
  );

  // Dynamic Fields Logic
  const getDynamicFields = () => {
    if (!propertyType || !lookingFor) return null;

    const fields = [];
    const config = {
      Purchase: {
        dimensions: ['Plot', 'Rental with Duplex Building', 'Duplex House', 'Land', 'PG', 'Office', 'Flat', 'Villas', 'Commercial Building', 'Apartment', 'Rental Building'],
        acres: ['Land'],
        budgetPerAcre: ['Land'],
        rentalIncome: ['PG', 'Office', 'Flat', 'Villas', 'Commercial Building', 'Apartment', 'Rental Building'],
        bedrooms: ['Flat', 'Villas']
      },
      Rent: {
        bedrooms: ['House', 'Duplex House', 'Villa'],
        monthlyRent: ['*'],
        dimensions: ['Commercial Plot', 'Commercial Building', 'Commercial Land', 'PG', 'Office', 'Flat'],
        acres: ['Commercial Land'],
        area: ['Office', 'Flat']
      },
      Lease: {
        bedrooms: ['House', 'Duplex House', 'Villa'],
        leasePerYear: ['*'],
        dimensions: ['Plot', 'Commercial Building', 'Commercial Plot', 'Commercial Land', 'PG', 'Office', 'Flat'],
        monthlyRent: ['Commercial Plot', 'Commercial Land'],
        acres: ['Commercial Land'],
        area: ['Office', 'Flat']
      },
      'JV/JD': {
        acres: ['Land'],
        dimensions: ['Plot'],
        ratio: ['*']
      },
      'Build to Suit': {
        acres: ['Land'],
        dimensions: ['Plot'],
        floor: ['Plot']
      }
    };

    const currentConfig = config[lookingFor];
    if (!currentConfig) return fields;

    Object.entries(currentConfig).forEach(([fieldType, propertyTypes]) => {
      if (propertyTypes.includes('*') || propertyTypes.includes(propertyType)) {
        switch (fieldType) {
          case 'dimensions':
            fields.push(renderDimensionFields());
            break;
          case 'acres':
            fields.push(renderRangeFields('min_acres', 'max_acres', 'Acres'));
            break;
          case 'monthlyRent':
            fields.push(renderRangeFields('min_monthly_rent', 'max_monthly_rent', 'Monthly Rent'));
            break;
          case 'leasePerYear':
            fields.push(renderRangeFields('min_lease_per_year', 'max_lease_per_year', 'Lease per Year'));
            break;
          case 'budgetPerAcre':
            fields.push(renderTextField('budget_per_acre', 'Budget per Acre', 'e.g., 500000'));
            break;
          case 'rentalIncome':
            fields.push(renderTextField('expected_rental_income', 'Expected Rental Income', 'e.g., 50000'));
            break;
          case 'bedrooms':
            fields.push(renderBedroomField());
            break;
          case 'ratio':
            fields.push(renderTextField('ratio', 'Ratio', 'e.g., 60:40'));
            break;
          case 'floor':
            fields.push(renderTextField('floor', 'Floor', 'e.g., 3'));
            break;
          case 'area':
            fields.push(renderTextField('area', 'Area (sq ft)', 'e.g., 1200'));
            break;
          default:
            break;
        }
      }
    });

    return fields;
  };

  return (
    <>
      <SearchBar onBackClick={handleBackClick} />
    <Box sx={{ padding: '16px', backgroundColor: '#f5f1eb', minHeight: '100vh', height: '100%', marginTop:"50px" }}>
      <FilterContainer>
        <SectionTitle>Looking For</SectionTitle>
        <FormControl fullWidth sx={{ mb: 2 }} size="small">
          <InputLabel>Select Type</InputLabel>
          <Select value={lookingFor} label="Select Type" onChange={(e) => handleLookingForChange(e.target.value)}>
            {LOOKING_FOR_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
          </Select>
        </FormControl>

        <SectionTitle>Property Type</SectionTitle>
        <FormControl fullWidth sx={{ mb: 2 }} size="small">
          <InputLabel>Select Property Type</InputLabel>
          <Select value={propertyType} label="Select Property Type" onChange={(e) => setPropertyType(e.target.value)} disabled={!lookingFor}>
            {lookingFor && PROPERTY_TYPE_OPTIONS[lookingFor]?.map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
          </Select>
        </FormControl>

        <SectionTitle>Areas/Cities</SectionTitle>
        <Autocomplete 
          multiple 
          freeSolo 
          options={options} 
          value={locations} 
          onChange={handleLocationChange}
          onInputChange={(e, value) => fetchPlaces(value)} 
          renderTags={(value, getTagProps) => value.map((option, index) => (
            <Chip variant="filled" label={option} {...getTagProps({ index })} sx={{ backgroundColor: "#8B4513", color: "white" }} />
          ))} 
          renderInput={(params) => <TextField {...params} label="Areas / Colonies / Streets" size="small" />} 
        />

        <SectionTitle sx={{ mt: 2 }}>Budget Range</SectionTitle>
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Min Budget</InputLabel>
              <Select value={minBudget} label="Min Budget" onChange={(e) => setMinBudget(e.target.value)} disabled={!lookingFor}>
                {lookingFor && BUDGET_RANGES[lookingFor]?.map((budget, index) => <MenuItem key={index} value={budget}>{budget}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Max Budget</InputLabel>
              <Select value={maxBudget} label="Max Budget" onChange={(e) => setMaxBudget(e.target.value)} disabled={!lookingFor || !minBudget}>
                {getAvailableMaxBudgets().map((budget, index) => <MenuItem key={index} value={budget}>{budget}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {getDynamicFields()}

        <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
          <Button variant="outlined" fullWidth onClick={handleCancel} sx={{ borderRadius: '8px', borderColor: '#8B4513', color: '#8B4513',
            '&:hover': { borderColor: '#654321', backgroundColor: 'rgba(139, 69, 19, 0.04)' } }}> Cancel </Button>
          <StyledButton fullWidth onClick={handleSave}> Save </StyledButton>
        </Stack>
      </FilterContainer>
    </Box>
    <FormsBottomNavbar />
    </>
  );
};

export default PGRoom;



// Without Optimized code 

// import React, { useContext, useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import {
//   Box,
//   Typography,
//   TextField,
//   Button,
//   Select,
//   MenuItem,
//   InputLabel,
//   FormControl,
//   Paper,
//   Stack,
//   Chip,
//   Slider,
//   FormControlLabel,
//   Checkbox,
//   Autocomplete
// } from '@mui/material';
// import { styled } from '@mui/material/styles';
// import FormsBottomNavbar from './FormsBottomNavbar';
// import SearchBar from './PostBack';
// import { AuthContext } from '../../AuthContext/AuthContext';
// import { GOOGLEMAPS_API_KEY } from '../../Api/ApiUrls';

// const FilterContainer = styled(Paper)(({ theme }) => ({
//   padding: '16px',
//   margin: '8px',
//   borderRadius: '12px',
//   boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
//   backgroundColor: '#fff',
//    minHeight: "75vh"
// }));

// const SectionTitle = styled(Typography)(({ theme }) => ({
//   fontWeight: 'bold',
//   fontSize: '16px',
//   marginBottom: '12px',
//   color: '#333'
// }));

// const StyledButton = styled(Button)(({ theme }) => ({
//   backgroundColor: '#8B4513',
//   color: 'white',
//   fontWeight: 'bold',
//   borderRadius: '8px',
//   padding: '8px 16px',
//   '&:hover': {
//     backgroundColor: '#654321',
//   },
// }));

// const PGRoom = () => {
//   const { userId } = useContext(AuthContext);
//   const [lookingFor, setLookingFor] = useState('');
//   const [locations, setLocations] = useState([]);
//   const [propertyType, setPropertyType] = useState('');
//    const [options, setOptions] = useState([]);

//   // ✅ Load Google Maps Script Automatically
//   useEffect(() => {
//     if (!window.google) {
//       const script = document.createElement("script");
//       script.src =
//         `https://maps.googleapis.com/maps/api/js?key=${GOOGLEMAPS_API_KEY}&libraries=places`;
//       script.async = true;
//       document.body.appendChild(script);
//     }
//   }, []);

//   // ✅ Fetch colonies, areas, full addresses
//   const fetchPlaces = (input) => {
//     if (!input || !window.google) return;

//     const service = new window.google.maps.places.AutocompleteService();

//     service.getPlacePredictions(
//       {
//         input,
//         types: ["geocode"], // 🔥 colonies, streets, door numbers
//         componentRestrictions: { country: "IN" }, // 🇮🇳 India only
//       },
//       (predictions) => {
//         if (predictions) {
//           setOptions(predictions.map((p) => p.description));
//         }
//       }
//     );
//   };
//   const [budgetRange, setBudgetRange] = useState([23000000, 50000000]);
//   const [useCurrentLocation, setUseCurrentLocation] = useState(false);
//   const [minBudget, setMinBudget] = useState('');
//   const [maxBudget, setMaxBudget] = useState('');
//   const [formData, setFormData] = useState({
//     length: '',
//     width: '',
//     units: 'ft',
//     area: '',
//     no_of_bedrooms: '',
//     min_monthly_rent: '',
//     max_monthly_rent: '',
//     min_lease_per_year: '',
//     max_lease_per_year: '',
//     min_acres: '',
//     max_acres: '',
//     budget_per_acre: '',
//     ratio: '',
//     floor: '',
//     expected_rental_income: ''
//   });
//   const navigate = useNavigate();

//   // Options from Excel data
//   const lookingForOptions = ['Purchase', 'Rent', 'Lease', 'JV/JD', 'Build to Suit'];
//   const locationOptions = ['Bangalore', 'Hyderabad', 'Delhi', 'Mumbai', 'Chennai'];
//   const propertyTypeOptions = {
//     'Purchase': ['Plot', 'Land', 'Rental with Duplex Building', 'Duplex House', 'PG', 'Office', 'Flat', 'Villas', 'Commercial Building', 'Apartment', 'Rental Building'],
//     'Rent': ['House', 'Duplex House', 'Villa', 'Commercial Plot', 'Commercial Land', 'Commercial Building', 'PG', 'Office', 'Flat'],
//     'Lease': ['House', 'Duplex House', 'Villa', 'Plot', 'Commercial Plot', 'Commercial Land', 'Commercial Building', 'PG', 'Office', 'Flat'],
//     'JV/JD': ['Land', 'Plot'],
//     'Build to Suit': ['Plot', 'Land']
//   };

//   // Budget ranges from second image
//   const budgetRanges = {
//     'Purchase': [
//       'No Min', '10L', '20L', '30L', '40L', '50L', '60L', '70L', '80L', '90L', 
//       '1CR', '1.25CR', '1.5CR', '1.75CR', '2CR', '2.5CR', '3CR', '3.5 CR', '4CR', '4.5CR', 
//       '5.5CR', '5.5CR', '6 CR.', '8CR', '9CR', '10CR', '15CR', '20CR', '25CR', '30CR', 
//       '40CR', '50CR', '60CR', '70CR', '80CR', '90CR', '100CR+++'
//     ],
//     'Rent': [
//       'No Min', '1K', '2K', '3K', '4K', '5K', '10K', '15K', '20K', '25K', '30K', 
//       '35K', '40K', '50K', '60K', '70K', '80K', '90K', '1L', '1.25L', '1.5 L', 
//       '2L', '2.5L', '3L', '3.5L', '4L', '4.5L', '5L', '6L', '7L', '8L', '9L', 
//       '10L', '15L', '20L', '25L', '30L', '30+++'
//     ],
//     'Lease': [
//       'No Min', '1L', '2L', '3L', '4L', '5L', '10L', '15L', '20L', '25L', '30L', 
//       '35L', '40L', '50L', '60L', '70L', '80L', '90L', '1CR', '1.25CR', '1.5 CR', 
//       '2CR', '2.5CR', '3CR', '3.5CR', '4CR', '4.5CR', '5CR', '6CR', '7CR', '8CR', 
//       '9CR', '10CR', '15CR', '20CR', '25CR', '30CR', '30+++'
//     ],
//     'JV/JD': [
//       'No Min', '1L', '2L', '3L', '4L', '5L', '10L', '15L', '20L', '25L', '30L', 
//       '32L', '40L', '50L', '60L', '70L', '80L', '90L', '1CR', '1.25CR', '1.5 CR', 
//       '2CR', '2.5CR', '3CR', '3.5CR', '4CR', '4.5CR', '5CR', '6CR', '7CR', '8CR', 
//       '9CR', '10CR', '15CR', '20CR', '25CR', '30CR', '30+++'
//     ],
//     'Build to Suit': [
//       'No Min', '1L', '2L', '3L', '4L', '5L', '10L', '11L', '12L', '21L', '22L', 
//       '31L', '32L', '40L', '50L', '60L', '70L', '80L', '90L', '1CR', '1.25CR', 
//       '1.5 CR', '2CR', '2.5CR', '3CR', '3.5CR', '4CR', '4.5CR', '5CR', '6CR', 
//       '7CR', '8CR', '9CR', '10CR', '15CR', '20CR', '25CR', '30CR', '30+++'
//     ]
//   };

//   const handleLocationChange = (event) => {
//     const {
//       target: { value },
//     } = event;
//     setLocations(typeof value === 'string' ? value.split(',') : value);
//   };

//   const handleBudgetChange = (event, newValue) => {
//     setBudgetRange(newValue);
//   };

//   const handleFormDataChange = (field, value) => {
//     setFormData(prev => ({
//       ...prev,
//       [field]: value
//     }));
//   };

//   const convertBudgetToNumber = (budgetStr) => {
//     if (!budgetStr || budgetStr === 'No Min') return 0;
    
//     if (budgetStr.includes('K')) {
//       return parseFloat(budgetStr) * 1000;
//     } else if (budgetStr.includes('L')) {
//       return parseFloat(budgetStr) * 100000;
//     } else if (budgetStr.includes('CR')) {
//       return parseFloat(budgetStr) * 10000000;
//     } else if (budgetStr.includes('+++')) {
//       // For maximum values, return a very high number
//       const num = parseFloat(budgetStr);
//       return num * 10000000; // Assuming CR for +++ values
//     }
//     return parseFloat(budgetStr);
//   };

//  const handleSave = async () => {
//   console.log("📌 Saving property request...");

//   try {
//     const payload = {
//       looking_for: lookingFor.toLowerCase(),
//       location: locations.join(', '),
//       // location: locations.map((l) => l.full ?? l),
//       property_type: propertyType,
//       length: parseFloat(formData.length) || 0,
//       width: parseFloat(formData.width) || 0,
//       units: formData.units,
//       area: parseFloat(formData.area) || 0,
//       min_budget: convertBudgetToNumber(minBudget),
//       max_budget: convertBudgetToNumber(maxBudget),
//       no_of_bedrooms: parseInt(formData.no_of_bedrooms) || 0,
//       min_monthly_rent: parseFloat(formData.min_monthly_rent) || 0,
//       max_monthly_rent: parseFloat(formData.max_monthly_rent) || 0,
//       min_year_lease: parseFloat(formData.min_lease_per_year) || 0,
//       max_year_lease: parseFloat(formData.max_lease_per_year) || 0,
//       min_acres: parseFloat(formData.min_acres) || 0,
//       max_acres: parseFloat(formData.max_acres) || 0,
//       ratio: formData.ratio,
//       floor: formData.floor || 0,
//       comment: "Property request from form",
//       user_id: userId
//     };

//     console.log("📤 Payload being sent:", payload);

//     const response = await fetch("https://landnest.net:81/property-request/", {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify(payload),
//     });

//     console.log("📥 Response status:", response.status);

//     if (response.ok) {
//       const result = await response.json();
//       console.log("✅ Property request saved successfully:", result);

//       navigate(-1);
//     } else {
//       const errorText = await response.text();
//       console.log("❌ Failed to save property request:", errorText);
//     }
//   } catch (error) {
//     console.log("🚨 Error saving property request:", error);
//   }
// };

//   const handleCancel = () => {
//     navigate(-1);
//   };

//   const formatBudget = (value) => {
//     if (value >= 10000000) {
//       return `${(value / 10000000).toFixed(1)} Cr`;
//     }
//     return `${(value / 100000).toFixed(0)} L`;
//   };

//   const getDynamicFields = () => {
//     if (!propertyType || !lookingFor) return null;

//     const fields = [];
    
//     // Purchase specific fields
//     if (lookingFor === 'Purchase') {
//       // Length * Width fields for purchase property types
//       if (['Plot', 'Rental with Duplex Building', 'Duplex House', 'Land', 'PG', 'Office', 'Flat', 'Villas', 'Commercial Building', 'Apartment', 'Rental Building'].includes(propertyType)) {
//         fields.push(
//           <Box key="dimensions" sx={{ mb: 2 }}>
//             <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
//               Dimensions
//             </Typography>
//             <Stack direction="row" spacing={1}>
//               <TextField
//                 fullWidth
//                 label="Length"
//                 placeholder="e.g., 40"
//                 variant="outlined"
//                 size="small"
//                 value={formData.length}
//                 onChange={(e) => handleFormDataChange('length', e.target.value)}
//               />
//               <Typography sx={{ alignSelf: 'center' }}>×</Typography>
//               <TextField
//                 fullWidth
//                 label="Width"
//                 placeholder="e.g., 60"
//                 variant="outlined"
//                 size="small"
//                 value={formData.width}
//                 onChange={(e) => handleFormDataChange('width', e.target.value)}
//               />
//               <TextField
//                 select
//                 label="Units"
//                 value={formData.units}
//                 size="small"
//                 sx={{ minWidth: 80 }}
//                 onChange={(e) => handleFormDataChange('units', e.target.value)}
//               >
//                 <MenuItem value="ft">ft</MenuItem>
//                 <MenuItem value="m">m</MenuItem>
//               </TextField>
//             </Stack>
//           </Box>
//         );
//       }

//       // Acres field for Land (Purchase) - Now with Min and Max
//       if (propertyType === 'Land') {
//         fields.push(
//           <Box key="acres" sx={{ mb: 2 }}>
//             <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
//               Acres
//             </Typography>
//             <Stack direction="row" spacing={1}>
//               <TextField
//                 fullWidth
//                 label="Min Acres"
//                 placeholder="e.g., 10"
//                 variant="outlined"
//                 size="small"
//                 value={formData.min_acres}
//                 onChange={(e) => handleFormDataChange('min_acres', e.target.value)}
//               />
//               <TextField
//                 fullWidth
//                 label="Max Acres"
//                 placeholder="e.g., 15"
//                 variant="outlined"
//                 size="small"
//                 value={formData.max_acres}
//                 onChange={(e) => handleFormDataChange('max_acres', e.target.value)}
//               />
//             </Stack>
//           </Box>
//         );
//       }

//       // Budget per acre for Land (Purchase)
//       if (propertyType === 'Land') {
//         fields.push(
//           <TextField
//             key="budgetPerAcre"
//             fullWidth
//             label="Budget per Acre"
//             placeholder="e.g., 500000"
//             variant="outlined"
//             size="small"
//             sx={{ mb: 2 }}
//             value={formData.budget_per_acre}
//             onChange={(e) => handleFormDataChange('budget_per_acre', e.target.value)}
//           />
//         );
//       }

//       // Rental Income for PG and Office (Purchase)
//       if (['PG', 'Office'].includes(propertyType)) {
//         fields.push(
//           <TextField
//             key="rentalIncome"
//             fullWidth
//             label="Expected Rental Income"
//             placeholder="e.g., 50000"
//             variant="outlined"
//             size="small"
//             sx={{ mb: 2 }}
//             value={formData.expected_rental_income}
//             onChange={(e) => handleFormDataChange('expected_rental_income', e.target.value)}
//           />
//         );
//       }

//       // Bedrooms and Rental Income for Flat and Villas (Purchase)
//       if (['Flat', 'Villas'].includes(propertyType)) {
//         fields.push(
//           <FormControl fullWidth key="bedrooms" sx={{ mb: 2 }} size="small">
//             <InputLabel>Number of Bedrooms</InputLabel>
//             <Select 
//               label="Number of Bedrooms"
//               value={formData.no_of_bedrooms}
//               onChange={(e) => handleFormDataChange('no_of_bedrooms', e.target.value)}
//             >
//               <MenuItem value="1">1 BHK</MenuItem>
//               <MenuItem value="2">2 BHK</MenuItem>
//               <MenuItem value="3">3 BHK</MenuItem>
//               <MenuItem value="4">4+ BHK</MenuItem>
//             </Select>
//           </FormControl>
//         );
//         fields.push(
//           <TextField
//             key="rentalIncome"
//             fullWidth
//             label="Expected Rental Income"
//             placeholder="e.g., 50000"
//             variant="outlined"
//             size="small"
//             sx={{ mb: 2 }}
//             value={formData.expected_rental_income}
//             onChange={(e) => handleFormDataChange('expected_rental_income', e.target.value)}
//           />
//         );
//       }

//       // Rental Income only for Commercial Building, Apartment, Rental Building (Purchase)
//       if (['Commercial Building', 'Apartment', 'Rental Building'].includes(propertyType)) {
//         fields.push(
//           <TextField
//             key="rentalIncome"
//             fullWidth
//             label="Expected Rental Income"
//             placeholder="e.g., 50000"
//             variant="outlined"
//             size="small"
//             sx={{ mb: 2 }}
//             value={formData.expected_rental_income}
//             onChange={(e) => handleFormDataChange('expected_rental_income', e.target.value)}
//           />
//         );
//       }
//     }

//     // Rent specific fields
//     if (lookingFor === 'Rent') {
//       // Bedrooms for residential properties (Rent)
//       if (['House', 'Duplex House', 'Villa'].includes(propertyType)) {
//         fields.push(
//           <FormControl fullWidth key="bedrooms" sx={{ mb: 2 }} size="small">
//             <InputLabel>Number of Bedrooms</InputLabel>
//             <Select 
//               label="Number of Bedrooms"
//               value={formData.no_of_bedrooms}
//               onChange={(e) => handleFormDataChange('no_of_bedrooms', e.target.value)}
//             >
//               <MenuItem value="1">1 BHK</MenuItem>
//               <MenuItem value="2">2 BHK</MenuItem>
//               <MenuItem value="3">3 BHK</MenuItem>
//               <MenuItem value="4">4+ BHK</MenuItem>
//             </Select>
//           </FormControl>
//         );
//       }

//       // Monthly Rent for all rent properties - Now with Min and Max
//       fields.push(
//         <Box key="monthlyRent" sx={{ mb: 2 }}>
//           <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
//             Monthly Rent
//           </Typography>
//           <Stack direction="row" spacing={1}>
//             <TextField
//               fullWidth
//               label="Min Monthly Rent"
//               placeholder="e.g., 20000"
//               variant="outlined"
//               size="small"
//               value={formData.min_monthly_rent}
//               onChange={(e) => handleFormDataChange('min_monthly_rent', e.target.value)}
//             />
//             <TextField
//               fullWidth
//               label="Max Monthly Rent"
//               placeholder="e.g., 30000"
//               variant="outlined"
//               size="small"
//               value={formData.max_monthly_rent}
//               onChange={(e) => handleFormDataChange('max_monthly_rent', e.target.value)}
//             />
//           </Stack>
//         </Box>
//       );

//       // Dimensions for Commercial Plot and Commercial Building (Rent)
//       if (['Commercial Plot', 'Commercial Building', 'Commercial Land', 'PG', 'Office', 'Flat'].includes(propertyType)) {
//         fields.push(
//           <Box key="dimensions" sx={{ mb: 2 }}>
//             <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
//               Dimensions
//             </Typography>
//             <Stack direction="row" spacing={1}>
//               <TextField
//                 fullWidth
//                 label="Length"
//                 placeholder="e.g., 40"
//                 variant="outlined"
//                 size="small"
//                 value={formData.length}
//                 onChange={(e) => handleFormDataChange('length', e.target.value)}
//               />
//               <Typography sx={{ alignSelf: 'center' }}>×</Typography>
//               <TextField
//                 fullWidth
//                 label="Width"
//                 placeholder="e.g., 60"
//                 variant="outlined"
//                 size="small"
//                 value={formData.width}
//                 onChange={(e) => handleFormDataChange('width', e.target.value)}
//               />
//               <TextField
//                 select
//                 label="Units"
//                 value={formData.units}
//                 size="small"
//                 sx={{ minWidth: 80 }}
//                 onChange={(e) => handleFormDataChange('units', e.target.value)}
//               >
//                 <MenuItem value="ft">ft</MenuItem>
//                 <MenuItem value="m">m</MenuItem>
//               </TextField>
//             </Stack>
//           </Box>
//         );
//       }

//       // Acres for Commercial Land (Rent) - Now with Min and Max
//       if (propertyType === 'Commercial Land') {
//         fields.push(
//           <Box key="acres" sx={{ mb: 2 }}>
//             <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
//               Acres
//             </Typography>
//             <Stack direction="row" spacing={1}>
//               <TextField
//                 fullWidth
//                 label="Min Acres"
//                 placeholder="e.g., 10"
//                 variant="outlined"
//                 size="small"
//                 value={formData.min_acres}
//                 onChange={(e) => handleFormDataChange('min_acres', e.target.value)}
//               />
//               <TextField
//                 fullWidth
//                 label="Max Acres"
//                 placeholder="e.g., 15"
//                 variant="outlined"
//                 size="small"
//                 value={formData.max_acres}
//                 onChange={(e) => handleFormDataChange('max_acres', e.target.value)}
//               />
//             </Stack>
//           </Box>
//         );
//       }

//       // Area for Office and Flat (Rent)
//       if (['Office', 'Flat'].includes(propertyType)) {
//         fields.push(
//           <TextField
//             key="area"
//             fullWidth
//             label="Area (sq ft)"
//             placeholder="e.g., 1200"
//             variant="outlined"
//             size="small"
//             sx={{ mb: 2 }}
//             value={formData.area}
//             onChange={(e) => handleFormDataChange('area', e.target.value)}
//           />
//         );
//       }
//     }

//     // Lease specific fields
//     if (lookingFor === 'Lease') {
//       // Bedrooms for residential properties (Lease)
//       if (['House', 'Duplex House', 'Villa'].includes(propertyType)) {
//         fields.push(
//           <FormControl fullWidth key="bedrooms" sx={{ mb: 2 }} size="small">
//             <InputLabel>Number of Bedrooms</InputLabel>
//             <Select 
//               label="Number of Bedrooms"
//               value={formData.no_of_bedrooms}
//               onChange={(e) => handleFormDataChange('no_of_bedrooms', e.target.value)}
//             >
//               <MenuItem value="1">1 BHK</MenuItem>
//               <MenuItem value="2">2 BHK</MenuItem>
//               <MenuItem value="3">3 BHK</MenuItem>
//               <MenuItem value="4">4+ BHK</MenuItem>
//             </Select>
//           </FormControl>
//         );
//       }

//       // Lease per year for all lease properties - Now with Min and Max
//       fields.push(
//         <Box key="leasePerYear" sx={{ mb: 2 }}>
//           <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
//             Lease per Year
//           </Typography>
//           <Stack direction="row" spacing={1}>
//             <TextField
//               fullWidth
//               label="Min Lease per Year"
//               placeholder="e.g., 200000"
//               variant="outlined"
//               size="small"
//               value={formData.min_lease_per_year}
//               onChange={(e) => handleFormDataChange('min_lease_per_year', e.target.value)}
//             />
//             <TextField
//               fullWidth
//               label="Max Lease per Year"
//               placeholder="e.g., 300000"
//               variant="outlined"
//               size="small"
//               value={formData.max_lease_per_year}
//               onChange={(e) => handleFormDataChange('max_lease_per_year', e.target.value)}
//             />
//           </Stack>
//         </Box>
//       );

//       // Dimensions for Plot and Commercial Building (Lease)
//       if (['Plot', 'Commercial Building', 'Commercial Plot', 'Commercial Land', 'PG', 'Office', 'Flat'].includes(propertyType)) {
//         fields.push(
//           <Box key="dimensions" sx={{ mb: 2 }}>
//             <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
//               Dimensions
//             </Typography>
//             <Stack direction="row" spacing={1}>
//               <TextField
//                 fullWidth
//                 label="Length"
//                 placeholder="e.g., 40"
//                 variant="outlined"
//                 size="small"
//                 value={formData.length}
//                 onChange={(e) => handleFormDataChange('length', e.target.value)}
//               />
//               <Typography sx={{ alignSelf: 'center' }}>×</Typography>
//               <TextField
//                 fullWidth
//                 label="Width"
//                 placeholder="e.g., 60"
//                 variant="outlined"
//                 size="small"
//                 value={formData.width}
//                 onChange={(e) => handleFormDataChange('width', e.target.value)}
//               />
//               <TextField
//                 select
//                 label="Units"
//                 value={formData.units}
//                 size="small"
//                 sx={{ minWidth: 80 }}
//                 onChange={(e) => handleFormDataChange('units', e.target.value)}
//               >
//                 <MenuItem value="ft">ft</MenuItem>
//                 <MenuItem value="m">m</MenuItem>
//               </TextField>
//             </Stack>
//           </Box>
//         );
//       }

//       // Monthly Rent for Commercial Plot and Commercial Land (Lease) - Now with Min and Max
//       if (['Commercial Plot', 'Commercial Land'].includes(propertyType)) {
//         fields.push(
//           <Box key="monthlyRent" sx={{ mb: 2 }}>
//             <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
//               Monthly Rent
//             </Typography>
//             <Stack direction="row" spacing={1}>
//               <TextField
//                 fullWidth
//                 label="Min Monthly Rent"
//                 placeholder="e.g., 20000"
//                 variant="outlined"
//                 size="small"
//                 value={formData.min_monthly_rent}
//                 onChange={(e) => handleFormDataChange('min_monthly_rent', e.target.value)}
//               />
//               <TextField
//                 fullWidth
//                 label="Max Monthly Rent"
//                 placeholder="e.g., 30000"
//                 variant="outlined"
//                 size="small"
//                 value={formData.max_monthly_rent}
//                 onChange={(e) => handleFormDataChange('max_monthly_rent', e.target.value)}
//               />
//             </Stack>
//           </Box>
//         );
//       }

//       // Acres for Commercial Land (Lease) - Now with Min and Max
//       if (propertyType === 'Commercial Land') {
//         fields.push(
//           <Box key="acres" sx={{ mb: 2 }}>
//             <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
//               Acres
//             </Typography>
//             <Stack direction="row" spacing={1}>
//               <TextField
//                 fullWidth
//                 label="Min Acres"
//                 placeholder="e.g., 10"
//                 variant="outlined"
//                 size="small"
//                 value={formData.min_acres}
//                 onChange={(e) => handleFormDataChange('min_acres', e.target.value)}
//               />
//               <TextField
//                 fullWidth
//                 label="Max Acres"
//                 placeholder="e.g., 15"
//                 variant="outlined"
//                 size="small"
//                 value={formData.max_acres}
//                 onChange={(e) => handleFormDataChange('max_acres', e.target.value)}
//               />
//             </Stack>
//           </Box>
//         );
//       }

//       // Area for Office and Flat (Lease)
//       if (['Office', 'Flat'].includes(propertyType)) {
//         fields.push(
//           <TextField
//             key="area"
//             fullWidth
//             label="Area (sq ft)"
//             placeholder="e.g., 1200"
//             variant="outlined"
//             size="small"
//             sx={{ mb: 2 }}
//             value={formData.area}
//             onChange={(e) => handleFormDataChange('area', e.target.value)}
//           />
//         );
//       }
//     }

//     // JV/JD specific fields
//     if (lookingFor === 'JV/JD') {
//       // Acres for Land (JV/JD) - Now with Min and Max
//       if (propertyType === 'Land') {
//         fields.push(
//           <Box key="acres" sx={{ mb: 2 }}>
//             <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
//               Acres
//             </Typography>
//             <Stack direction="row" spacing={1}>
//               <TextField
//                 fullWidth
//                 label="Min Acres"
//                 placeholder="e.g., 10"
//                 variant="outlined"
//                 size="small"
//                 value={formData.min_acres}
//                 onChange={(e) => handleFormDataChange('min_acres', e.target.value)}
//               />
//               <TextField
//                 fullWidth
//                 label="Max Acres"
//                 placeholder="e.g., 15"
//                 variant="outlined"
//                 size="small"
//                 value={formData.max_acres}
//                 onChange={(e) => handleFormDataChange('max_acres', e.target.value)}
//               />
//             </Stack>
//           </Box>
//         );
//       }

//       // Dimensions for Plot (JV/JD) - Now with both Length and Width
//       if (propertyType === 'Plot') {
//         fields.push(
//           <Box key="dimensions" sx={{ mb: 2 }}>
//             <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
//               Dimensions
//             </Typography>
//             <Stack direction="row" spacing={1}>
//               <TextField
//                 fullWidth
//                 label="Length"
//                 placeholder="e.g., 40"
//                 variant="outlined"
//                 size="small"
//                 value={formData.length}
//                 onChange={(e) => handleFormDataChange('length', e.target.value)}
//               />
//               <Typography sx={{ alignSelf: 'center' }}>×</Typography>
//               <TextField
//                 fullWidth
//                 label="Width"
//                 placeholder="e.g., 60"
//                 variant="outlined"
//                 size="small"
//                 value={formData.width}
//                 onChange={(e) => handleFormDataChange('width', e.target.value)}
//               />
//               <TextField
//                 select
//                 label="Units"
//                 value={formData.units}
//                 size="small"
//                 sx={{ minWidth: 80 }}
//                 onChange={(e) => handleFormDataChange('units', e.target.value)}
//               >
//                 <MenuItem value="ft">ft</MenuItem>
//                 <MenuItem value="m">m</MenuItem>
//               </TextField>
//             </Stack>
//           </Box>
//         );
//       }

//       // Ratio for all JV/JD properties
//       fields.push(
//         <TextField
//           key="ratio"
//           fullWidth
//           label="Ratio"
//           placeholder="e.g., 60:40"
//           variant="outlined"
//           size="small"
//           sx={{ mb: 2 }}
//           value={formData.ratio}
//           onChange={(e) => handleFormDataChange('ratio', e.target.value)}
//         />
//       );
//     }

//     // Build to Suit specific fields
//    // Build to Suit specific fields
// if (lookingFor === 'Build to Suit') {
//   // Acres for Land (Build to Suit) - Now with Min and Max
//   if (propertyType === 'Land') {
//     fields.push(
//       <Box key="acres" sx={{ mb: 2 }}>
//         <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
//           Acres
//         </Typography>
//         <Stack direction="row" spacing={1}>
//           <TextField
//             fullWidth
//             label="Min Acres"
//             placeholder="e.g., 10"
//             variant="outlined"
//             size="small"
//             value={formData.min_acres}
//             onChange={(e) => handleFormDataChange('min_acres', e.target.value)}
//           />
//           <TextField
//             fullWidth
//             label="Max Acres"
//             placeholder="e.g., 15"
//             variant="outlined"
//             size="small"
//             value={formData.max_acres}
//             onChange={(e) => handleFormDataChange('max_acres', e.target.value)}
//           />
//         </Stack>
//       </Box>
//     );
//   }

//   // Dimensions for Plot (Build to Suit) - Now with both Length and Width
//   if (propertyType === 'Plot') {
//     fields.push(
//       <Box key="dimensions" sx={{ mb: 2 }}>
//         <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
//           Dimensions
//         </Typography>
//         <Stack direction="row" spacing={1}>
//           <TextField
//             fullWidth
//             label="Length"
//             placeholder="e.g., 40"
//             variant="outlined"
//             size="small"
//             value={formData.length}
//             onChange={(e) => handleFormDataChange('length', e.target.value)}
//           />
//           <Typography sx={{ alignSelf: 'center' }}>×</Typography>
//           <TextField
//             fullWidth
//             label="Width"
//             placeholder="e.g., 60"
//             variant="outlined"
//             size="small"
//             value={formData.width}
//             onChange={(e) => handleFormDataChange('width', e.target.value)}
//           />
//           <TextField
//             select
//             label="Units"
//             value={formData.units}
//             size="small"
//             sx={{ minWidth: 80 }}
//             onChange={(e) => handleFormDataChange('units', e.target.value)}
//           >
//             <MenuItem value="ft">ft</MenuItem>
//             <MenuItem value="m">m</MenuItem>
//           </TextField>
//         </Stack>
//       </Box>
//     );
//     fields.push(
//       <TextField
//         key="floor"
//         fullWidth
//         label="Floor"
//         placeholder="e.g., 3"
//         variant="outlined"
//         size="small"
//         sx={{ mb: 2 }}
//         value={formData.floor}
//         onChange={(e) => handleFormDataChange('floor', e.target.value)}
//       />
//     );
//   }
// }
//     return fields;
//   };

  
//   const handleBackClick = () => navigate(-1);

//   return (
//      <>
//      <SearchBar onBackClick={handleBackClick} />

//     <Box sx={{ 
//       padding: '16px', 
//       backgroundColor: '#f5f1eb',
//       minHeight: '100vh',
//       height: '100%',
//       marginTop: "15%"
//     }}>
//       <FilterContainer>
//         {/* Looking For - Moved to top */}
//         <SectionTitle>Looking For</SectionTitle>
//         <FormControl fullWidth sx={{ mb: 2 }} size="small">
//           <InputLabel>Select Type</InputLabel>
//           <Select
//             value={lookingFor}
//             label="Select Type"
//             onChange={(e) => {
//               setLookingFor(e.target.value);
//               setPropertyType('');
//               setMinBudget('');
//               setMaxBudget('');
//               // Reset form data when lookingFor changes
//               setFormData({
//                 length: '',
//                 width: '',
//                 units: 'ft',
//                 area: '',
//                 no_of_bedrooms: '',
//                 min_monthly_rent: '',
//                 max_monthly_rent: '',
//                 min_lease_per_year: '',
//                 max_lease_per_year: '',
//                 min_acres: '',
//                 max_acres: '',
//                 budget_per_acre: '',
//                 ratio: '',
//                 floor: '',
//                 expected_rental_income: ''
//               });
//             }}
//           >
//             {lookingForOptions.map((option) => (
//               <MenuItem key={option} value={option}>{option}</MenuItem>
//             ))}
//           </Select>
//         </FormControl>

//         {/* Property Type - Moved to top */}
//         <SectionTitle>Property Type</SectionTitle>
//         <FormControl fullWidth sx={{ mb: 2 }} size="small">
//           <InputLabel>Select Property Type</InputLabel>
//           <Select
//             value={propertyType}
//             label="Select Property Type"
//             onChange={(e) => setPropertyType(e.target.value)}
//             disabled={!lookingFor}
//           >
//             {lookingFor && propertyTypeOptions[lookingFor]?.map((type) => (
//               <MenuItem key={type} value={type}>{type}</MenuItem>
//             ))}
//           </Select>
//         </FormControl>

//         {/* Location */}
//         <SectionTitle>Areas/Cities</SectionTitle>
//         <Autocomplete
//       multiple
//       freeSolo
//       options={options}
//       value={locations}
//       onChange={(event, newValue) => setLocations(newValue)}
//       onInputChange={(e, value) => fetchPlaces(value)}
//       renderTags={(value, getTagProps) =>
//         value.map((option, index) => (
//           <Chip
//             variant="filled"
//             label={option}
//             {...getTagProps({ index })}
//             sx={{ backgroundColor: "#8B4513", color: "white" }}
//           />
//         ))
//       }
//       renderInput={(params) => (
//         <TextField {...params} label="Areas / Colonies / Streets" size="small" />
//       )}
//     />

//         {/* Budget Range - Updated with two input fields */}
//         <SectionTitle>Budget Range</SectionTitle>
//         <Box sx={{ mb: 3 }}>
//           <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
//             <FormControl fullWidth size="small">
//               <InputLabel>Min Budget</InputLabel>
//               <Select
//                 value={minBudget}
//                 label="Min Budget"
//                 onChange={(e) => setMinBudget(e.target.value)}
//                 disabled={!lookingFor}
//               >
//                 {lookingFor && budgetRanges[lookingFor]?.map((budget, index) => (
//                   <MenuItem key={index} value={budget}>{budget}</MenuItem>
//                 ))}
//               </Select>
//             </FormControl>
//             <FormControl fullWidth size="small">
//               <InputLabel>Max Budget</InputLabel>
//               <Select
//                 value={maxBudget}
//                 label="Max Budget"
//                 onChange={(e) => setMaxBudget(e.target.value)}
//                 disabled={!lookingFor}
//               >
//                 {lookingFor && budgetRanges[lookingFor]?.map((budget, index) => (
//                   <MenuItem key={index} value={budget}>{budget}</MenuItem>
//                 ))}
//               </Select>
//             </FormControl>
//           </Stack>
//         </Box>

//         {/* Dynamic Fields */}
//         {getDynamicFields()}

//         {/* Action Buttons */}
//         <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
//           <Button 
//             variant="outlined" 
//             fullWidth
//             onClick={handleCancel}
//             sx={{ 
//               borderRadius: '8px',
//               borderColor: '#8B4513',
//               color: '#8B4513',
//               '&:hover': {
//                 borderColor: '#654321',
//                 backgroundColor: 'rgba(139, 69, 19, 0.04)'
//               }
//             }}
//           >
//             Cancel
//           </Button>
//           <StyledButton fullWidth onClick={handleSave}>
//             Save
//           </StyledButton>
//         </Stack>
//       </FilterContainer>
//      </Box>
//        <FormsBottomNavbar />
//     </>
//   );
// };

// export default PGRoom;